import type { CategoryItem } from '../../../gateways/breadstack/categoryApis'

export interface CategoryLookups {
  nameById: Map<number, string>
  /** Full ancestor breadcrumb, e.g. "A > B > C". */
  pathById: Map<number, string>
  countById: Map<number, number>
  imageById: Map<number, string>
}

const pickImageSrc = (v: unknown): string => {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object' && 'src' in v) {
    const src = (v as { src?: unknown }).src
    return typeof src === 'string' ? src : ''
  }
  return ''
}

// Builds numeric-id keyed lookups used by every excluded-categories surface
// (Pickup / Delivery cards, Mail Order shipping zones). Callers should wrap
// this in `useMemo` keyed on `categories`.
export function buildCategoryLookups(
  categories: CategoryItem[],
): CategoryLookups {
  const nameById = new Map<number, string>()
  const countById = new Map<number, number>()
  const imageById = new Map<number, string>()
  const byId = new Map<number, CategoryItem>()

  categories.forEach((c) => {
    const id = Number(c.id)
    byId.set(id, c)
    nameById.set(id, c.name)
    countById.set(id, c.productCount ?? 0)
    const raw = (c as { image?: unknown }).image
    const thumb = (c as { thumbnail?: unknown }).thumbnail
    const src = pickImageSrc(raw) || pickImageSrc(thumb)
    if (src) imageById.set(id, src)
  })

  // Walk the parentId chain up to the root; guards against missing/cyclic
  // parents.
  const pathById = new Map<number, string>()
  categories.forEach((c) => {
    const names: string[] = []
    const seen = new Set<number>()
    let current: CategoryItem | undefined = c
    while (current && !seen.has(Number(current.id))) {
      seen.add(Number(current.id))
      names.unshift(current.name)
      const parentId = current.parentId == null ? null : Number(current.parentId)
      current = parentId != null ? byId.get(parentId) : undefined
    }
    pathById.set(Number(c.id), names.join(' > '))
  })

  return { nameById, pathById, countById, imageById }
}

// child id -> parent id (numeric). Roots are absent.
export function buildParentMap(categories: CategoryItem[]): Map<number, number> {
  const map = new Map<number, number>()
  categories.forEach((c) => {
    if (c.parentId != null && c.parentId !== '') {
      map.set(Number(c.id), Number(c.parentId))
    }
  })
  return map
}

// True when any ancestor (parent, grandparent, …) of `id` is in `ancestorIds`.
// Excluding a category on the backend also blocks all of its descendants.
export function hasAncestorIn(
  id: number,
  ancestorIds: Set<number>,
  parentMap: Map<number, number>,
): boolean {
  const seen = new Set<number>([id])
  let current = parentMap.get(id)
  while (current != null && !seen.has(current)) {
    if (ancestorIds.has(current)) return true
    seen.add(current)
    current = parentMap.get(current)
  }
  return false
}
